<template>
    <div class="comments-login">
        <div class="content login-card animated fadeIn">
            <h2 v-if="!user && !hideTitle">Login</h2>

            <div v-if="!user" class="login-buttons">
                <!-- OAuth Buttons (always shown) -->
                <button @click="login('google')" class="google-btn">
                    <img src="/assets/GoogleLogo.svg" alt="Google" class="icon" style="background: rgb(255, 255, 255); border-radius: 20px;" />
                    Continue with Google
                </button>
                <button @click="login('microsoft')" class="microsoft-btn">
                    <img src="/assets/MicrosoftLogo.svg" alt="Microsoft" class="icon" />
                    Continue with Microsoft
                </button>

                <!-- Email + Password Login -->
                <div v-if="allowEmailLogin && mode === 'login'" class="mt-3">
                    <input type="email" v-model="email" placeholder="Email" required class="form-input" />
                    <input type="password" v-model="password" placeholder="Password" required class="form-input" />
                    <button @click="submitLogin" :disabled="loggingIn">Login</button>
                </div>

                <!-- Sign Up Form -->
                <div v-if="allowSignup && mode === 'signup'" class="mt-3">
                    <input type="text" v-model="name" placeholder="Name" required class="form-input" />
                    <input type="email" v-model="email" placeholder="Email" required class="form-input" />
                    <input type="password" v-model="password" placeholder="Password" required class="form-input" />
                    <button @click="submitSignup" :disabled="signingUp">Sign Up</button>
                </div>

                <!-- Guest Option -->
                <div v-if="allowGuest && mode === 'login'">
                    <button @click="showGuestForm = !showGuestForm" class="btn btn-warning guest-btn">
                        Continue as Guest
                    </button>
                    <div v-if="showGuestForm" class="guest-form">
                        <input v-model="guest.name" placeholder="Name" required />
                        <input v-model="guest.email" type="email" placeholder="Email" required />
                        <button @click="continueAsGuest" class="confirm-guest-btn btn btn-success">Start Checkout</button>
                    </div>
                </div>

                <!-- Toggle -->
                <p v-if="error" :class="error.startsWith('Signup successful') ? 'text-success' : 'text-danger'" class="small mt-2">{{ error }}</p>

                <div class="text-center">

                    <p class="small-toggle text-center mt-3">
                        <span v-if="mode === 'login'">
                            Don’t have an account?
                            <a href="#" @click.prevent="mode = 'signup'">Sign up</a>
                        </span>
                        <span v-else>
                            Already have an account?
                            <a href="#" @click.prevent="mode = 'login'">Log in</a>
                        </span>
                    </p>
                </div>
            </div>




            <div v-if="user" class="user-info">
                <p>Welcome, <strong>{{ user.user }}</strong></p>
                <p>{{ user.email }}</p>
                <button @click="logout" class="logout-btn">Logout</button>
            </div>
        </div>
    </div>

</template>

<script>
    import { mapState } from 'pinia';
    import { useRoute, useRouter } from "vue-router";
    import { useAuthStore } from "../../utils/authStore";
    import { fetchWithAuth } from "../../utils/authFetch";

    export default {
        components: {
            useRoute,
            useRouter
        },
        props: {
            allowGuest: {
                type: Boolean,
                default: false
            },
            allowEmailLogin: Boolean,
            hideTitle: Boolean,
            allowSignup: Boolean
        },
        data() {
            return {
                mode: 'login',
                showGuestForm: false,
                guest: {
                    name: '',
                    email: ''
                },
                name: '',
                email: '',
                password: '',
                error: '',
                signingUp: false
            };
        },
        computed: {
            ...mapState(useAuthStore, ['user']),
            isAdmin() {
                return this.user?.roles?.includes("Admin");
            },
            authStore() {
                return useAuthStore();
            },
        },
        watch: {
            '$route.query.token'(newToken) {
                if (newToken) {
                    const refreshToken = this.$route.query.refreshtoken;
                    console.log("Token appeared later:", newToken)
                    if (refreshToken) {
                        localStorage.setItem("refreshtoken", refreshToken);
                        console.log("refreshtoken Token Stored!");
                    }

                    // Handle token from query params
                    localStorage.setItem("jwt", newToken);
                    console.log("JWT Token Stored!");
                    this.$router.replace({ path: this.$route.path, query: {} }); // Remove query params
                    this.getCurrentUser();
                }
            }
        },
        methods: {
            async submitSignup() {
                this.error = "";
                try {
                    this.signingUp = true;
                    const verificationUrl = `${window.location.origin}/verify-email`;
                    const res = await fetch("/auth/signup", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            name: this.name,
                            email: this.email,
                            password: this.password,
                            isPartner: false,
                            verificationUrl
                        })
                    });

                    if (!res.ok) {
                        this.error = await res.text();
                        return;
                    }

                    // ✅ Show success message and switch to login mode
                    this.mode = 'login';
                    this.error = "Signup successful! Please check your email to verify your account.";
                    this.name = "";
                    this.email = "";
                    this.password = "";
                } catch (err) {
                    this.error = "Something went wrong. Try again.";
                } finally {
                    this.signingUp = false;
                }
            },
            async submitLogin() {
                this.error = '';
                this.loggingIn = true;
                try {
                    const res = await fetch('/auth/manual-login', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ email: this.email, password: this.password })
                    });

                    if (!res.ok) {
                        this.error = await res.text();
                        return;
                    }

                    const data = await res.json();
                    localStorage.setItem("jwt", data.accessToken);
                    localStorage.setItem("refreshToken", data.refreshToken);
                    await this.getCurrentUser();
                } catch (err) {
                    this.error = "Login failed. Try again.";
                } finally {
                    this.loggingIn = false;
                }
            },
            async getCurrentUser() {
                await this.authStore.fetchUser();

            },
            login(provider) {
                const baseUrl = import.meta.env.PROD
                    ? 'https://hub.pittroche.co.uk'
                    : 'https://localhost:7113';
                const returnUrl = encodeURIComponent(window.location.pathname);
                const domain = window.location.origin;
                window.location.href = `${baseUrl}/auth/login/${provider}?returnUrl=${domain}${returnUrl}`;
            },
            async logout() {
                this.authStore.logout();
                
                this.$router.replace({ path: route.path, query: {} }); // Remove query params
            },
            handleOAuthRedirect() {
                const route = useRoute();
                const router = useRouter();
                //console.log("Mounted Login");
                //console.log(route);

                if (route.query.refreshtoken) {
                    localStorage.setItem("refreshtoken", route.query.refreshtoken);
                    console.log("refreshtoken Token Stored!");
                }

                // Handle token from query params
                if (route.query.token) {
                    localStorage.setItem("jwt", route.query.token);
                    console.log("JWT Token Stored!");
                    router.replace({ path: route.path, query: {} }); // Remove query params
                    this.getCurrentUser();
                }


            },
            async continueAsGuest() {
                try {
                    if (!this.guest.name || !this.guest.email) {
                        this.guestError = "Please provide both name and email.";
                        return;
                    }

                    const response = await fetch('/auth/guestcheckout', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(this.guest)
                    });

                    if (!response.ok) throw new Error('Guest login failed');
                    const data = await response.json();

                    localStorage.setItem("jwt", data.token);
                    localStorage.setItem("refreshtoken", data.refreshToken);
                    console.log("Guest token stored");

                    await this.getCurrentUser();

                } catch (err) {
                    console.error(err);
                }
            }
        },
        mounted() {
            this.handleOAuthRedirect(); // Handle auth redirect
        },
        setup() {

        }
    };
</script>

<style scoped>
    .comments-login {
        display: flex;
        justify-content: center;
        align-items: center;
        margin-top: 20px;
        margin-bottom: 20px;
        align-items: baseline;
    }

    .login-card {
        padding: 20px;
        border-radius: 5px;
        text-align: center;
        max-width: 350px;
        min-width: 350px;
        width: 100%;
    }

    h2 {
        font-size: 20px;
        margin-bottom: 15px;
    }

    .login-buttons {
        display: flex;
        flex-direction: column;
        gap: 10px;
    }



    .google-btn {
        background: #4285F4;
    }

    .microsoft-btn {
        background: #2F2F2F;
    }

    .logout-btn {
        background: red;
        margin-top: 15px;
    }

    .user-info p {
        font-size: 14px;
        margin: 5px 0;
    }

    .icon {
        width: 20px;
        height: 20px;
        padding: 5px;
    }

    @media (max-width: 1024px) {
        .login-card {
            max-width: inherit;
            min-width: inherit;
        }
    }

    .guest-btn {
        background: #777;
    }

    .guest-form {
        display: flex;
        flex-direction: column;
        gap: 8px;
        margin-top: 10px;
    }

        .guest-form input {
            padding: 8px;
            border-radius: 6px;
            border: 1px solid #ccc;
        }

    .confirm-guest-btn {
        background: #4caf50;
    }

    button {
        color: #fff;
        background-color: #167c98;
        padding: 15px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        border: none;
        cursor: pointer;
        font-size: 16px;
        width: 100%;
        border-radius: 6px;
        transition: all .3s ease-in-out;
        font-weight: 700;
    }
    .content {
        background-color: transparent;
    }
</style>
